import { useRef } from "react";
import { motion, useScroll, useReducedMotion } from "framer-motion";
import { EASE_STAGE, STAGE_STEP } from "../lib/constants";

/**
 * Sequence. Vertical timeline layout for "What We Build" and "How We Work".
 * Renders as a node + line spine:
 *   - A single continuous vertical line on the left, drawn top-down via
 *     `pathLength` bound to the section's scroll progress.
 *   - A gold dot marker anchored to each stage card.
 *
 * Reduced-motion: the spine line is drawn to full length statically and per-node
 * reveal transitions are skipped.
 *
 * Props:
 *  - items: [{ name, hint, detail }]
 *  - variant: "build" | "flow"
 *  - startIndex: number to start numbering from (default 1)
 */
export function Sequence({ items, variant = "build", startIndex = 1 }) {
  const containerRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  // Scroll progress across the sequence container. The line pathLength is
  // driven directly by this MotionValue so the draw follows the user's scroll.
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 85%", "end 65%"],
  });

  return (
    <div
      ref={containerRef}
      className="sequence spine"
      role="list"
      data-variant={variant}
      data-testid={`sequence-${variant}`}
    >
      <div className="spine-track" aria-hidden="true">
        <svg
          className="spine-svg"
          viewBox="0 0 4 100"
          preserveAspectRatio="none"
          data-testid={`spine-svg-${variant}`}
        >
          <motion.line
            x1="2"
            y1="0"
            x2="2"
            y2="100"
            stroke="#D4AF37"
            strokeOpacity="0.55"
            strokeWidth="1.6"
            strokeLinecap="round"
            style={prefersReducedMotion ? { pathLength: 1 } : { pathLength: scrollYProgress }}
            data-testid={`spine-line-${variant}`}
          />
        </svg>
      </div>

      {items.map((it, i) => (
        <motion.div
          key={it.name}
          className="sequence-node"
          role="listitem"
          tabIndex={0}
          data-testid={`sequence-node-${variant}-${i}`}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{
            duration: 0.7,
            ease: EASE_STAGE,
            delay: i * STAGE_STEP,
          }}
        >
          <span className="spine-dot" aria-hidden="true" data-testid={`spine-dot-${variant}-${i}`} />
          <span className="stage-num">
            {String(startIndex + i).padStart(2, "0")}
          </span>
          <h3 className="stage-name">{it.name}</h3>
          {it.hint && <div className="stage-hint">{it.hint}</div>}
          {it.detail && <p className="stage-body">{it.detail}</p>}
        </motion.div>
      ))}
    </div>
  );
}
